import React from "react";
import { Text, View } from "react-native";
import { EStyleSheet, useDynamicStyles } from "react-native-extended-stylesheet-typescript";
import { CheckmarkIcon } from "../icons/CheckmarkIcon";
import { XIcon } from "../icons/XIcon";
import { GlobalStyles, Transition } from "../style/Style";

interface ButtonProps {
    buttonText: string;
    width: number;
    height?: number;
    isActive: boolean;
    index: number;
    resulted: boolean;
    winIndex: number | null;
    isWinner: boolean;
}

export const SimplePropButtonDisplay: React.FC<ButtonProps> = ({
    buttonText,
    width,
    height,
    index,
    isActive,
    isWinner,
    winIndex,
    resulted,
}) => {
    const maxWidth = isActive ? width + 30 : width;
    const styles = useDynamicStyles(() => GenerateStyles(maxWidth, isActive));
    const containerWidth = width ? { maxWidth: width } : {};
    const containerHeight = height ? { maxHeight: height } : {};

    let icon: JSX.Element;
    let outlineStyle;

    if (isActive) {
        icon = (
            <View style={styles.icon}>
                <CheckmarkIcon fill={EStyleSheet.value(GlobalStyles.PROP_SELECTED_ICON)} />
            </View>
        );
        outlineStyle = styles.activeButtonColor;
    } else {
        icon = <></>;
    }

    if (resulted) {
        if (winIndex === index) {
            icon = (
                <View style={[styles.icon, styles.won]}>
                    <CheckmarkIcon fill={EStyleSheet.value(GlobalStyles.PROP_SELECTED_ICON)} />
                </View>
            );
            outlineStyle = styles.wonOutline;
        } else {
            icon = (
                <View style={[styles.icon, styles.lost]}>
                    <XIcon fill={EStyleSheet.value(GlobalStyles.PROP_SELECTED_ICON)} />
                </View>
            );
            outlineStyle = styles.lostOutline;
        }
    }

    if (resulted && !isActive) {
        icon = <></>;
        if (winIndex !== index) {
            outlineStyle = {};
        }
    }

    return (
        <View style={styles.buttonContainer}>
            <View
                style={[
                    styles.button,
                    containerWidth,
                    containerHeight,
                    isActive && styles.activeButtonColor,
                    outlineStyle,
                    Transition(),
                ]}
            >
                <Text style={[styles.text, isActive && styles.activeTextBorder]}>{buttonText}</Text>
            </View>
            {icon}
        </View>
    );
};

const GenerateStyles = (width: number, isSelected: boolean) => {
    return EStyleSheet.create({
        buttonContainer: {
            flexDirection: "row",
            width: "100%",
            borderColor: "transparent",
            height: 30,
            marginHorizontal: 2.5,
            maxWidth: width,
            justifyContent: "center",
        },
        button: {
            flex: 1,
            borderColor: "transparent",
            backgroundColor: () => EStyleSheet.value(GlobalStyles.PROP_BACKGROUND),
            borderWidth: () => EStyleSheet.value(GlobalStyles.BORDER_THICKNESS),
            borderRadius: () => EStyleSheet.value(GlobalStyles.BUTTON_RADIUS),
            fontFamily: () => EStyleSheet.value(GlobalStyles.HEADER_FONT),
            textAlign: "center",
            height: 30,
            fontSize: 16,
            justifyContent: "center",
        },
        text: {
            borderColor: "transparent",
            borderWidth: () => EStyleSheet.value(GlobalStyles.BORDER_THICKNESS),
            borderRadius: () => EStyleSheet.value(GlobalStyles.BUTTON_RADIUS),
            fontSize: "inherit",
            color: "inherit",
            height: 26,
            lineHeight: 26,
            fontFamily: "inherit",
        },
        activeTextBorder: {
            borderColor: () => EStyleSheet.value(GlobalStyles.BACKGROUND_COLOUR),
            borderTopRightRadius: 0,
            borderBottomRightRadius: 0,
        },
        activeButtonColor: {
            borderColor: () => EStyleSheet.value(GlobalStyles.PROP_SELECTED),
            backgroundColor: () => EStyleSheet.value(GlobalStyles.PROP_SELECTED_BACKGROUND),
            color: () => EStyleSheet.value(GlobalStyles.PROP_SELECTED_TEXT),
            borderTopRightRadius: 0,
            borderBottomRightRadius: 0,
        },
        disabledButtonColor: {
            backgroundColor: () => EStyleSheet.value(GlobalStyles.DISABLED_BUTTON),
            color: () => EStyleSheet.value(GlobalStyles.DISABLED_TEXT),
        },
        won: {
            backgroundColor: () => EStyleSheet.value(GlobalStyles.WINNING_PROP),
        },
        lost: {
            backgroundColor: () => EStyleSheet.value(GlobalStyles.LOSING_PROP),
        },
        wonOutline: {
            borderColor: () => EStyleSheet.value(GlobalStyles.WINNING_PROP),
        },
        lostOutline: {
            borderColor: () => EStyleSheet.value(GlobalStyles.LOSING_PROP),
        },
        icon: {
            width: 30,
            height: "100%",
            alignContent: "center",
            justifyContent: "center",
            alignItems: "center",
            borderTopRightRadius: () => EStyleSheet.value(GlobalStyles.PROP_BORDER_RADIUS),
            borderBottomRightRadius: () => EStyleSheet.value(GlobalStyles.PROP_BORDER_RADIUS),
            backgroundColor: isSelected
                ? () => EStyleSheet.value(GlobalStyles.PROP_SELECTED)
                : () => EStyleSheet.value(GlobalStyles.PROP_BACKGROUND),
        },
    });
};
